import { useIntl } from '@/intl'
import TopNavigationBarItem from '@/scopes/common/components/Header/TopNavigationBarItem'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Badge, Icon, IconGlyph, Popover, Sheet } from '@sunwebgroup/ui-components/uds'
import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { styled } from 'styled-components'

const Root = styled(Popover.Root)`
  [data-radix-popper-content-wrapper] {
    z-index: 99 !important;
  }
`

const PopoverContent = styled(Popover.Content)`
  @media (max-width: ${tokPx('primitives_breakpoint_s_max')}) {
    ${Popover.Body} {
      overflow-y: hidden;
    }
  }
  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: 380px !important; //didn't find a better way to do it due to specificity from rules in UDS Popover
  }
`

const Wrapper = styled(TopNavigationBarItem)`
  position: relative;
  padding: ${space('primitives_space_8')};

  ${Badge} {
    color: ${tok('semantics_content_white_color')};
    background-color: ${tok('semantics_indicators_accents_background_color')};
    position: absolute;
    top: 1px;
    right: 1px;
    width: 20px;
    height: 20px;
    min-width: 20px;
  }
`

const PopoverTrigger = styled(Popover.Trigger)`
  width: 40px;
  height: 40px;
  position: relative;

  padding: ${space('primitives_space_8')};
  border-radius: ${tokPx('semantics_buttons_m_corner_radius')};
  background: none;
  border: none;
  display: flex;
  align-items: center;
  cursor: pointer;

  &:hover {
    color: ${tok('semantics_content_color')};
    background: ${tok('primitives_color_neutral_100')};
  }

  &[data-state='open'] {
    background: ${tok('primitives_color_neutral_150')};
  }
`

export function Item({ a11yDescription, onOpenChange, iconGlyph, open, children, count, asSheet = false, title }: PropsWithChildren<{
  open: boolean
  iconGlyph: IconGlyph
  a11yDescription: string
  onOpenChange: (open: boolean) => void
  count?: number
  asSheet?: boolean
  title?: string
}>) {
  const intl = useIntl()
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const IconTrigger = useMemo(() => {
    if (count && hasMounted) {
      return (
        <Wrapper>
          <Icon glyph={iconGlyph} size={24} />
          <Badge variant="body-m-bold-tight">{count}</Badge>
        </Wrapper>
      )
    }
    else {
      return (
        <Wrapper>
          <Icon glyph={iconGlyph} size={24} />
        </Wrapper>
      )
    }
  }, [hasMounted, count, iconGlyph])

  return (
    <>
      {asSheet
        ? (
            <Sheet.Root open={open} onOpenChange={onOpenChange}>
              <Sheet.Trigger aria-label={a11yDescription}>
                {IconTrigger}
              </Sheet.Trigger>
              <Sheet.Body side="right" hasPadding bottomSideInMobile>
                <Sheet.Header closeLabel={intl.formatMessage({ id: 'close' })} titleLabel={title} />
                {children}
              </Sheet.Body>
            </Sheet.Root>
          )
        : (
            <Root open={open} onOpenChange={onOpenChange}>
              <PopoverTrigger aria-label={a11yDescription}>
                {IconTrigger}
              </PopoverTrigger>
              <PopoverContent position="top-left" hasPadding fullHeight closeText={intl.formatMessage({ id: 'close' })}>
                {children}
              </PopoverContent>
            </Root>
          )}
    </>
  )
}
